import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

export function AdminDepartmentEditPage() {
  const { id: idParam } = useParams<{ id?: string }>();
  const isNew = !idParam;
  const id = idParam ? parseInt(idParam, 10) : null;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [budget, setBudget] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin-departments'],
    queryFn: () => api.listDepartments(),
    enabled: !isNew,
  });
  const existing = !isNew && listData ? listData.rows.find((d) => d.id === id) : undefined;

  const { data: allCardsData } = useQuery({
    queryKey: ['admin-cards'],
    queryFn: () => api.listCards(),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing) {
      setCode(existing.code);
      setName(existing.name);
      setBudget(existing.budget_monthly_vnd?.toString() ?? '');
    }
  }, [existing]);

  const create = useMutation({
    mutationFn: (body: { code: string; name: string; budget_monthly_vnd: number | null }) =>
      api.createDepartment(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-departments'] });
      qc.invalidateQueries({ queryKey: ['departments-lookup'] });
      navigate('/admin/departments');
    },
    onError: (e) => setServerError(String(e).slice(0, 200)),
  });
  const update = useMutation({
    mutationFn: (body: { code?: string; name?: string; budget_monthly_vnd?: number | null }) =>
      api.updateDepartment(id as number, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-departments'] });
      qc.invalidateQueries({ queryKey: ['departments-lookup'] });
      navigate('/admin/departments');
    },
    onError: (e) => setServerError(String(e).slice(0, 200)),
  });

  const pending = create.isPending || update.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const payload = {
      code: code.trim(),
      name: name.trim(),
      budget_monthly_vnd: budget ? Number(budget) : null,
    };
    if (isNew) {
      create.mutate(payload);
    } else {
      update.mutate(payload);
    }
  }

  const ownedCards =
    !isNew && allCardsData ? allCardsData.rows.filter((c) => c.owning_department_id === id) : [];

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Link
        to="/admin/departments"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-card hover:text-foreground"
      >
        <ArrowLeft size={16} strokeWidth={1.5} />
        Back to list
      </Link>
      <PageHeader title={isNew ? 'Add department' : `Edit ${existing?.code ?? `#${id}`}`} />

      {!isNew && listLoading && <Skeleton className="h-40 w-full" />}

      {(isNew || existing) && (
        <Card>
          <form onSubmit={onSubmit} className="space-y-3 p-4 text-sm">
            <div className="space-y-1.5">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="SBU1, AI_CENTER"
                required
                pattern="[A-Z][A-Z0-9_]{1,15}"
                title="Uppercase A-Z, digits and underscore; 2-16 chars"
                className="font-mono"
              />
              <span className="block text-xs text-muted-foreground/70">
                Uppercase, A–Z, 0–9, underscore. 2–16 ký tự.
              </span>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="budget">Monthly budget VND (tuỳ chọn)</Label>
              <Input
                id="budget"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="20000000"
                min="0"
                step="1"
                className="tabular-nums"
              />
            </div>

            {!isNew && (
              <div className="space-y-1.5">
                <Label>Cards owned by this department</Label>
                {ownedCards.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Chưa có thẻ nào thuộc dept này. Vào{' '}
                    <Link to="/admin/cards" className="text-primary underline">
                      Cards
                    </Link>{' '}
                    để gán owning department cho từng thẻ.
                  </p>
                ) : (
                  <ul className="text-sm text-foreground">
                    {ownedCards.map((c) => (
                      <li key={c.id} className="flex items-center gap-2 py-0.5">
                        <span className="font-mono text-xs text-muted-foreground">
                          {c.bank_code} •{c.last4}
                        </span>
                        <span className="truncate">{c.alias}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {serverError && (
              <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                {serverError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => navigate('/admin/departments')}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" variant="default" size="sm" disabled={pending}>
                {pending ? 'Đang lưu…' : isNew ? 'Create' : 'Save'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
